import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { ShieldCheck, FileText, Trash2, Loader2, CheckCircle2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { errorText } from '../lib/errorText';

const API = process.env.REACT_APP_BACKEND_URL;

const LegalLayout = ({ title, subtitle, children, testid }) => (
  <div className="min-h-screen bg-[#002d2c] text-white" data-testid={testid}>
    <header className="border-b border-white/10">
      <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
        <Link to="/login" className="flex items-center gap-3"><img src="/brand/logo-horizontal-green.png" alt="DEAL" className="h-8 w-auto" /></Link>
        <nav className="flex gap-4 text-xs text-brand-mint/80">
          <Link to="/privacidad" data-testid="legal-nav-privacy" className="hover:text-brand-butter">Privacidad</Link>
          <Link to="/terminos" data-testid="legal-nav-terms" className="hover:text-brand-butter">Términos</Link>
          <Link to="/eliminar-datos" data-testid="legal-nav-deletion" className="hover:text-brand-butter">Eliminar datos</Link>
        </nav>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-5 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold text-brand-butter">{title}</h1>
      {subtitle && <p className="text-sm text-brand-mint/80 mt-2">{subtitle}</p>}
      <div className="mt-8 rounded-3xl bg-white text-[#0f2a28] p-6 sm:p-10 space-y-6 leading-relaxed">{children}</div>
      <p className="text-[11px] text-brand-mint/60 mt-8">© {new Date().getFullYear()} DEAL · una marca de Octopus Vision Company S.A.S. · <Link to="/login" className="underline">Volver a la app</Link></p>
    </main>
  </div>
);

const Sections = ({ doc }) => (
  <>
    <p className="text-sm">{doc.intro}</p>
    {doc.sections.map((s) => (
      <section key={s.title} className="space-y-2">
        <h2 className="text-base md:text-lg font-semibold text-[#002d2c]">{s.title}</h2>
        {s.content && <p className="text-sm">{s.content}</p>}
        {s.items && <ul className="list-disc pl-5 text-sm space-y-1">{s.items.map((it, i) => <li key={i}>{it}</li>)}</ul>}
      </section>
    ))}
    <p className="text-xs text-muted-foreground border-t pt-4">Última actualización: {doc.last_updated} · Contacto: <a className="underline" href={`mailto:${doc.contact}`}>{doc.contact}</a></p>
  </>
);

const useLegalDoc = (kind) => {
  const [doc, setDoc] = useState(null);
  useEffect(() => { axios.get(`${API}/api/legal/${kind}`).then(({ data }) => setDoc(data)).catch(() => setDoc(null)); }, [kind]);
  return doc;
};

export default function PrivacyPolicy() {
  const doc = useLegalDoc('privacy');
  return (
    <LegalLayout title="Política de Privacidad" subtitle="Cómo DEAL trata los datos de las empresas y de sus clientes" testid="privacy-page">
      {doc ? <Sections doc={doc} /> : <Loader2 className="w-5 h-5 animate-spin" />}
    </LegalLayout>
  );
}

export function TermsPage() {
  const doc = useLegalDoc('terms');
  return (
    <LegalLayout title="Términos y Condiciones" subtitle="Reglas de uso de la plataforma DEAL" testid="terms-page">
      {doc ? <Sections doc={doc} /> : <Loader2 className="w-5 h-5 animate-spin" />}
    </LegalLayout>
  );
}

export function DataDeletionPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: '', phone: '', platform_user_id: '', reason: '' });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState(params.get('code') || '');
  const [status, setStatus] = useState(null);

  const lookup = React.useCallback(async (c) => {
    if (!c) return;
    try { const { data } = await axios.get(`${API}/api/data-deletion/status/${c}`); setStatus(data); setError(''); }
    catch (e) { setStatus(null); setError(errorText(e, 'Código no encontrado')); }
  }, []);
  useEffect(() => { if (params.get('code')) lookup(params.get('code')); }, [params, lookup]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/data-deletion/request`, form);
      setResult(data); setCode(data.confirmation_code);
    } catch (e) { setError(errorText(e, 'No se pudo enviar la solicitud')); } finally { setBusy(false); }
  };

  const STATUS_LABEL = { received: 'Recibida · en proceso', completed: 'Completada · datos eliminados' };

  return (
    <LegalLayout title="Eliminación de datos" subtitle="Solicita que borremos tus datos personales de DEAL y consulta el estado con tu código" testid="data-deletion-page">
      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-semibold text-[#002d2c] flex items-center gap-2"><Trash2 className="w-4 h-4" /> ¿Qué eliminamos?</h2>
        <p className="text-sm">Tu perfil de cliente (nombre, teléfono, correo, identificadores de WhatsApp, Instagram o Messenger), todas tus conversaciones y la información comercial derivada de ellas. Si llegaste desde Facebook o Instagram, Meta también puede enviarnos la solicitud automáticamente al desconectar la app; recibirás un código de confirmación.</p>
      </section>

      {!result ? (
        <section className="space-y-3">
          <h2 className="text-base md:text-lg font-semibold text-[#002d2c]">Enviar solicitud</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Correo</Label><Input data-testid="deletion-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tu@correo.com" /></div>
            <div><Label>Teléfono (con código de país)</Label><Input data-testid="deletion-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+57 300 000 0000" /></div>
          </div>
          <div><Label>Usuario de Instagram / Facebook (opcional)</Label><Input data-testid="deletion-platform-id" value={form.platform_user_id} onChange={(e) => setForm({ ...form, platform_user_id: e.target.value })} /></div>
          <div><Label>Motivo (opcional)</Label><Textarea data-testid="deletion-reason" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          {error && <p className="text-sm text-red-600" data-testid="deletion-error">{error}</p>}
          <Button onClick={submit} disabled={busy || !(form.email || form.phone || form.platform_user_id)} className="bg-[#002d2c] text-brand-butter rounded-full" data-testid="deletion-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Solicitar eliminación'}
          </Button>
          <p className="text-xs text-muted-foreground">Respondemos en un máximo de 30 días. Guardaremos únicamente el código y la fecha de la solicitud como constancia.</p>
        </section>
      ) : (
        <section className="rounded-2xl bg-brand-mint p-5 space-y-2" data-testid="deletion-result">
          <p className="flex items-center gap-2 font-semibold text-[#002d2c]"><CheckCircle2 className="w-5 h-5 text-brand-green" /> Solicitud recibida</p>
          <p className="text-sm">Tu código de confirmación es <b data-testid="deletion-code">{result.confirmation_code}</b>. Guárdalo para consultar el estado.</p>
        </section>
      )}

      <section className="space-y-3 border-t pt-6">
        <h2 className="text-base md:text-lg font-semibold text-[#002d2c] flex items-center gap-2"><Search className="w-4 h-4" /> Consultar estado</h2>
        <div className="flex gap-2">
          <Input data-testid="deletion-status-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Código de confirmación" className="max-w-xs" />
          <Button variant="outline" onClick={() => lookup(code)} data-testid="deletion-status-btn" className="rounded-full">Consultar</Button>
        </div>
        {status && (
          <div className="text-sm rounded-2xl bg-muted/40 p-3" data-testid="deletion-status-result">
            <p><b>Estado:</b> {STATUS_LABEL[status.status] || status.status}</p>
            <p className="text-xs text-muted-foreground">Recibida: {new Date(status.created_at).toLocaleString('es')}{status.completed_at ? ` · Completada: ${new Date(status.completed_at).toLocaleString('es')}` : ''}</p>
          </div>
        )}
      </section>
      <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Más información en la <Link to="/privacidad" className="underline">Política de Privacidad</Link> y los <Link to="/terminos" className="underline"><FileText className="w-3 h-3 inline" /> Términos</Link>.</p>
    </LegalLayout>
  );
}
